import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ReportData, FindingConfig, DetailedFinding } from '../../types/report.types';
import { getGlobalAbbreviations, normalizeSeverity, getFindingStableKey } from '../../utils/findingsUtils';

interface FindingConfigEditorProps {
  reportData: ReportData;
  onDataChange: (field: string, value: any) => void;
  onFindingChange?: (index: number, field: keyof DetailedFinding, value: string) => void;
}

const SEVERITY_BADGES: Record<string, { bg: string; text: string; border: string; indicator: string }> = {
  Critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', indicator: 'bg-red-500' },
  High:     { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', indicator: 'bg-orange-500' },
  Medium:   { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', indicator: 'bg-amber-500' },
  Low:      { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', indicator: 'bg-blue-500' },
  Info:     { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', indicator: 'bg-slate-500' },
};

const STATUS_BADGES: Record<string, { bg: string; text: string; border: string }> = {
  'Open':                   { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  'Closed':                 { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  'Business Accepts Risk':   { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' },
  'Business Non-Actionable': { bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200' },
};

const FindingConfigEditor: React.FC<FindingConfigEditorProps> = ({
  reportData,
  onDataChange,
  onFindingChange
}) => {
  const findingsWithKeys = useMemo(() => {
    return (reportData.detailed_findings || []).map((f: any, index: number) => {
      const stableKey = getFindingStableKey(f, index);
      return { ...f, _stableKey: stableKey, _originalIndex: index };
    });
  }, [reportData.detailed_findings]);

  const [localConfig, setLocalConfig] = useState<FindingConfig>(() => reportData.finding_config || {
    globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true },
    individualFindings: {}
  });

  const lastExternalConfig = useRef<string>('');

  useEffect(() => {
    const externalConfigStr = JSON.stringify(reportData.finding_config || {});
    if (reportData.finding_config && externalConfigStr !== lastExternalConfig.current) {
      setLocalConfig(reportData.finding_config);
      lastExternalConfig.current = externalConfigStr;
    }
  }, [reportData.finding_config]);

  const [search, setSearch] = useState('');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const abbrMap = useMemo(() => getGlobalAbbreviations(reportData), [reportData]);

  const getStatus = (f: any) => {
    if (f.is_closed === true) return 'CLOSED';
    const s = String(f.status || '').toLowerCase().trim();
    if (['closed', 'resolved', 'fixed', 'remediated'].includes(s)) return 'CLOSED';
    if (s.includes('accept') || s.includes('risk')) return 'RISK';
    if (s.includes('non-actionable') || s.includes('non actionable')) return 'NON-ACTION';
    return 'OPEN';
  };

  const getNormalizedStatusValue = (f: any): string => {
    const s = String(f.status || '').toLowerCase().trim();
    if (['closed', 'resolved', 'fixed', 'remediated'].includes(s)) return 'Closed';
    if (s.includes('accept') || s.includes('risk')) return 'Business Accepts Risk';
    if (s.includes('non-actionable') || s.includes('non actionable')) return 'Business Non-Actionable';
    return 'Open';
  };

  const isIncludedByDefault = (f: any) => {
    return getStatus(f) === 'OPEN';
  };

  const sortedFindings = useMemo(() => {
    const sevOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
    return [...findingsWithKeys].sort((a, b) => {
      const aSev = normalizeSeverity(a.severity_category).toLowerCase();
      const bSev = normalizeSeverity(b.severity_category).toLowerCase();
      if (sevOrder[aSev] !== sevOrder[bSev]) return (sevOrder[aSev] || 99) - (sevOrder[bSev] || 99);
      const aAbbr = abbrMap[(a.title || '').toLowerCase().trim()] || a.abbreviation || '';
      const bAbbr = abbrMap[(b.title || '').toLowerCase().trim()] || b.abbreviation || '';
      return aAbbr.localeCompare(bAbbr, undefined, { numeric: true });
    });
  }, [findingsWithKeys, abbrMap]);

  const syncState = (newConfig: FindingConfig) => {
    lastExternalConfig.current = JSON.stringify(newConfig);
    setLocalConfig(newConfig);
    onDataChange('finding_config', newConfig);
  };

  const handleBulkAction = (action: 'open-only' | 'all' | 'none') => {
    const newIndividual: Record<string, any> = {};
    findingsWithKeys.forEach((f: any) => {
      let included = true;
      if (action === 'open-only') included = isIncludedByDefault(f);
      else if (action === 'none') included = false;

      newIndividual[f._stableKey] = {
        included,
        fields: { ...(localConfig.individualFindings[f._stableKey]?.fields || localConfig.globalFields) }
      };
    });
    syncState({ ...localConfig, individualFindings: newIndividual });
  };

  const toggleInclusion = (f: any) => {
    const id = f._stableKey;
    const current = localConfig.individualFindings[id] || { 
      included: isIncludedByDefault(f),
      fields: { ...localConfig.globalFields }
    };
    const newIndividual = { ...localConfig.individualFindings, [id]: { ...current, included: !current.included } };
    syncState({ ...localConfig, individualFindings: newIndividual });
  };

  const toggleField = (f: any, field: keyof FindingConfig['globalFields']) => {
    const id = f._stableKey;
    const current = localConfig.individualFindings[id] || { 
      included: isIncludedByDefault(f),
      fields: { ...localConfig.globalFields }
    };
    const newIndividual = { 
      ...localConfig.individualFindings, 
      [id]: { 
        ...current, 
        fields: { ...current.fields, [field]: !current.fields[field] } 
      } 
    };
    syncState({ ...localConfig, individualFindings: newIndividual });
  };

  const handleFindingStatusOrSeverityChange = (f: any, field: 'status' | 'severity_category', value: string) => {
    if (!onFindingChange) return;
    const actualIndex = (reportData.detailed_findings || []).findIndex((df: any, idx: number) => {
      return df === f || getFindingStableKey(df, idx) === f._stableKey || (df.title && f.title && df.title.toLowerCase().trim() === f.title.toLowerCase().trim());
    });
    if (actualIndex !== -1) {
      onFindingChange(actualIndex, field as keyof DetailedFinding, value);
    }
  };

  const currentBulkType = useMemo(() => {
    if (findingsWithKeys.length === 0) return 'none';
    
    const allIncluded = findingsWithKeys.every((f: any) => {
      const config = localConfig.individualFindings[f._stableKey];
      return config ? config.included : isIncludedByDefault(f);
    });
    if (allIncluded) return 'all';

    const matchesOpenOnly = findingsWithKeys.every((f: any) => {
      const config = localConfig.individualFindings[f._stableKey];
      const isIncluded = config ? config.included : isIncludedByDefault(f);
      return isIncluded === isIncludedByDefault(f);
    });
    if (matchesOpenOnly) return 'open-only';

    const noneIncluded = findingsWithKeys.every((f: any) => {
      const config = localConfig.individualFindings[f._stableKey];
      return config ? !config.included : !isIncludedByDefault(f);
    });
    if (noneIncluded) return 'none';

    return 'custom';
  }, [findingsWithKeys, localConfig]);

  const filteredFindings = useMemo(() => {
    return sortedFindings.filter((f: any) => {
      const s = search.toLowerCase();
      const matchesSearch = !search || f.title?.toLowerCase().includes(s) || (f.abbreviation && f.abbreviation.toLowerCase().includes(s));
      const fSev = normalizeSeverity(f.severity_category).toLowerCase();
      return matchesSearch && (filterSeverity === 'all' || fSev === filterSeverity.toLowerCase());
    });
  }, [sortedFindings, search, filterSeverity]);

  const includedCount = useMemo(() => {
    return findingsWithKeys.filter((f: any) => {
      const config = localConfig.individualFindings[f._stableKey];
      return config ? config.included : isIncludedByDefault(f);
    }).length;
  }, [findingsWithKeys, localConfig]);

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Header Banner */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <span>⚙️ Finding Configuration &amp; Status Manager</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Change severity, status, and PDF inclusion for all report findings. Changes recalculate all report counts automatically.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              {includedCount} of {findingsWithKeys.length} Included in Report
            </span>
          </div>
        </div>

        {/* Controls Toolbar */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          {/* Quick Selection Pills */}
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => handleBulkAction('open-only')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                currentBulkType === 'open-only' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Open Only
            </button>
            <button
              onClick={() => handleBulkAction('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                currentBulkType === 'all' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Include All
            </button>
            <button
              onClick={() => handleBulkAction('none')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                currentBulkType === 'none' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-600 hover:text-red-600'
              }`}
            >
              Deselect All
            </button>
          </div>

          {/* Filters & Search */}
          <div className="flex items-center gap-2 flex-1 max-w-md justify-end">
            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              className="px-2.5 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg bg-white text-slate-700 outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical Only</option>
              <option value="high">High Only</option>
              <option value="medium">Medium Only</option>
              <option value="low">Low Only</option>
              <option value="info">Info Only</option>
            </select>

            <div className="relative flex-1 max-w-xs">
              <svg className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search findings..."
                className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-800 outline-none focus:ring-2 focus:ring-blue-400"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Findings List (Card View for Maximum Clarity) */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {filteredFindings.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
            <p className="text-sm font-medium">No findings match your search or filter.</p>
          </div>
        ) : (
          filteredFindings.map((f: any) => {
            const config = localConfig.individualFindings[f._stableKey] || { included: isIncludedByDefault(f), fields: { ...localConfig.globalFields } };
            const sev = normalizeSeverity(f.severity_category);
            const currentStatusVal = getNormalizedStatusValue(f);
            const badge = SEVERITY_BADGES[sev] || SEVERITY_BADGES.Info;
            const statusBadge = STATUS_BADGES[currentStatusVal] || STATUS_BADGES.Open;
            const abbr = abbrMap[(f.title || '').toLowerCase().trim()] || f.abbreviation || '??';

            return (
              <div
                key={f._stableKey}
                className={`bg-white rounded-xl border transition-all shadow-sm p-4 ${
                  !config.included
                    ? 'border-slate-200 bg-slate-50/70 opacity-60'
                    : 'border-slate-200 hover:border-blue-300 hover:shadow-md'
                }`}
              >
                {/* Header Row */}
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                    {/* Included Checkbox */}
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={config.included}
                        onChange={() => toggleInclusion(f)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="text-xs font-bold text-slate-700">
                        {config.included ? 'Included' : 'Excluded'}
                      </span>
                    </label>

                    {/* Abbreviation Badge */}
                    <span className={`px-2.5 py-1 rounded-md text-xs font-black border ${badge.bg} ${badge.text} ${badge.border} flex items-center gap-1.5`}>
                      <span className={`w-2 h-2 rounded-full ${badge.indicator}`} />
                      {abbr}
                    </span>

                    {/* Title */}
                    <h3 className={`text-sm font-bold leading-snug ${config.included ? 'text-slate-900' : 'text-slate-500 line-through'}`}>
                      {f.title}
                    </h3>
                  </div>
                </div>

                {/* Edit Controls Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-2 border-t border-slate-100 items-center">
                  {/* 1. Severity Dropdown */}
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Severity</label>
                    <select
                      value={sev}
                      onChange={(e) => handleFindingStatusOrSeverityChange(f, 'severity_category', e.target.value)}
                      className={`w-full text-xs font-bold border rounded-lg px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-blue-400 bg-white ${badge.text} ${badge.border}`}
                    >
                      <option value="Critical">Critical</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                      <option value="Info">Info</option>
                    </select>
                  </div>

                  {/* 2. Status Dropdown */}
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Status</label>
                    <select
                      value={currentStatusVal}
                      onChange={(e) => handleFindingStatusOrSeverityChange(f, 'status', e.target.value)}
                      className={`w-full text-xs font-bold border rounded-lg px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-blue-400 bg-white ${statusBadge.text} ${statusBadge.border}`}
                    >
                      <option value="Open">Open</option>
                      <option value="Closed">Closed</option>
                      <option value="Business Accepts Risk">Business Accepts Risk</option>
                      <option value="Business Non-Actionable">Business Non-Actionable</option>
                    </select>
                  </div>

                  {/* 3. Included PDF Sections */}
                  <div className="sm:col-span-2 md:col-span-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">PDF Detail Fields</label>
                    <div className={`flex flex-wrap gap-1 ${config.included ? 'opacity-100' : 'pointer-events-none'}`}>
                      {(Object.keys(config.fields) as Array<keyof FindingConfig['globalFields']>).map(field => {
                        const isEnabled = config.fields[field];
                        return (
                          <button
                            key={field}
                            type="button"
                            onClick={() => toggleField(f, field)}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all border ${
                              isEnabled
                                ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                                : 'bg-slate-100 text-slate-400 border-slate-200 hover:bg-slate-200'
                            }`}
                          >
                            {field.charAt(0).toUpperCase() + field.slice(1)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FindingConfigEditor;
